import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { useLookup, useWorkspace } from "@/hooks";
import FilterDateRangeSelector from "@/presentation/deduction/components/DeductionFilter/DateRangeSelector";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import FilterInfoCol from "@/presentation/deduction/components/DeductionFilter/DeductionFilterInfoCol";
import {
  DATE_CONSTANTS,
  buildDistributionCenterOptions,
  buildGLCodeOptions,
  buildPurchaserOptions,
  calculateFromDateByMonths,
} from "@/presentation/deduction/components/DeductionFilter/utils";
import { Button, DatePicker, Division, Flex, Form, Row, Select, Span } from "@ir/ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useApproval } from "../hooks";
import { mapBrandMembersToOptions } from "@/presentation/deduction/components/DeductionDrawer/DeductionInformation/edit/hooks/useDeductionDetailsOptions";
import { ApprovalFilterInfo } from "@/store/approvals";
import {
  useDateRangeHandlers,
  useDateRangeState,
  useDefaultFilterSync,
  useFormSync,
} from "@/presentation/deduction/components/DeductionFilter/hooks";
import { getEmptyApprovalFilterInfo, mapMonthsToFilterDateRange } from "@/store/deduction/deductionFilter/utils";

interface ApprovalFilterFormProps {
  closeFilter: () => void;
}

const dateService = new DateService(new DateAdapter());

const DEFAULT_CHECK_DATE_RANGE_IN_MONTHS = -1;
const IS_NO_DEFAULT_DATE_RANGE = DEFAULT_CHECK_DATE_RANGE_IN_MONTHS === -1;
const DEFAULT_CHECK_DATE_RANGE_PRESET = mapMonthsToFilterDateRange(DEFAULT_CHECK_DATE_RANGE_IN_MONTHS);
const DEFAULT_CHECK_FROM_DATE_BY_WORKSPACE = IS_NO_DEFAULT_DATE_RANGE
  ? null
  : calculateFromDateByMonths(DEFAULT_CHECK_DATE_RANGE_IN_MONTHS);

const DEFAULT_FILTER_INFO: ApprovalFilterInfo = {
  ...getEmptyApprovalFilterInfo(),
  checkFromDate: DEFAULT_CHECK_FROM_DATE_BY_WORKSPACE,
  checkToDate: IS_NO_DEFAULT_DATE_RANGE ? null : DATE_CONSTANTS.currentDateFormatted,
  checkDateRange: DEFAULT_CHECK_DATE_RANGE_PRESET,
};

const ApprovalFilterForm = ({ closeFilter }: ApprovalFilterFormProps) => {
  const [form] = Form.useForm();
  const { settings, workspace } = useWorkspace();

  // Lookup hooks
  const {
    getPurchasersLookup,
    purchasersLookup,
    getDistributionCentersLookup,
    distributionCentersLookup,
    getPrioritiesLookup,
    getValiditiesLookup,
    getAdjustmentTypesLookup,
    getAdjustmentCategoriesLookup,
    getRetailersLookup,
    glCodesLookup,
    getGLCodesLookup,
    getBrandMembersLookup,
    brandMembersLookup,
  } = useLookup();

  const {
    filterInfo,
    isFilterApplied,
    setIsFilterApplied,
    setFilter: setApprovalFilter,
    fetchApprovalsList,
    setSelectedApprovals,
    setCurrentPage,
  } = useApproval();

  // Initialize lookups on mount
  useEffect(() => {
    const workspaceKey = workspace?.workspaceKey;
    if (!workspaceKey) {
      return;
    }
    getPurchasersLookup();
    getDistributionCentersLookup();
    getPrioritiesLookup();
    getValiditiesLookup();
    getAdjustmentTypesLookup();
    getAdjustmentCategoriesLookup();
    getRetailersLookup();
    getGLCodesLookup();

    getBrandMembersLookup({ include_deleted: true, include_bots: false, role_key: [] });
  }, [
    getPurchasersLookup,
    getDistributionCentersLookup,
    getPrioritiesLookup,
    getValiditiesLookup,
    getAdjustmentTypesLookup,
    getAdjustmentCategoriesLookup,
    getRetailersLookup,
    getGLCodesLookup,
    getBrandMembersLookup,
    workspace?.workspaceKey,
  ]);

  const defaultFilterInfo = DEFAULT_FILTER_INFO;
  const defaultCheckDateRangePreset = DEFAULT_CHECK_DATE_RANGE_PRESET;
  const isNoDefaultDateRange = IS_NO_DEFAULT_DATE_RANGE;
  const defaultCheckFromDateByWorkspace = DEFAULT_CHECK_FROM_DATE_BY_WORKSPACE;

  // Form field watchers
  const checkFromDateValue = Form.useWatch("checkFromDate", form);
  const checkToDateValue = Form.useWatch("checkToDate", form);
  const invoiceFromDateValue = Form.useWatch("invoiceFromDate", form);
  const invoiceToDateValue = Form.useWatch("invoiceToDate", form);
  const purchaserFieldValue = Form.useWatch("purchaser", form);

  // Date constraints
  const checkFromDateMaxValue = checkToDateValue ?? DATE_CONSTANTS.checkFromDateDefaultMaxValue;
  const checkToDateMinValue = checkFromDateValue ?? defaultCheckFromDateByWorkspace ?? DATE_CONSTANTS.defaultFromDate;
  const invoiceFromDateMaxValue = invoiceToDateValue ?? DATE_CONSTANTS.invoiceFromDateDefaultMaxValue;
  const invoiceToDateMinValue = invoiceFromDateValue ?? DATE_CONSTANTS.invoiceFromDateDefaultMinValue;

  // Options builders
  const purchaserOptions = useMemo(() => buildPurchaserOptions(purchasersLookup?.data ?? []), [purchasersLookup?.data]);

  const dcOptions = useMemo(
    () =>
      buildDistributionCenterOptions(
        distributionCentersLookup?.data ?? [],
        purchaserFieldValue?.map((v: { value: string }) => v.value)
      ),
    [distributionCentersLookup?.data, purchaserFieldValue]
  );

  const glCodeOptions = useMemo(() => buildGLCodeOptions(glCodesLookup?.data ?? []), [glCodesLookup?.data]);
  const analystOptions = useMemo(
    () => mapBrandMembersToOptions(brandMembersLookup?.data?.filter(member => !member?.isDeleted) ?? []),
    [brandMembersLookup?.data]
  );
  const approverOptions = useMemo(
    () => mapBrandMembersToOptions(brandMembersLookup?.data?.filter(member => !member?.isDeleted) ?? []),
    [brandMembersLookup?.data]
  );

  // Handle field changes (clear date range when dates are manually changed)
  const onFieldsChange = (changedValues: Partial<ApprovalFilterInfo>) => {
    if (Object.hasOwn(changedValues, "checkFromDate")) {
      form.setFieldsValue({ checkDateRange: null });
      updateCheckDateRange(null);
      setApprovalFilter({
        ...filterInfo,
        checkDateRange: null,
        checkFromDate: changedValues.checkFromDate ?? null,
      });
    }

    if (Object.hasOwn(changedValues, "checkToDate")) {
      form.setFieldsValue({ checkDateRange: null });
      updateCheckDateRange(null);
      setApprovalFilter({
        ...filterInfo,
        checkDateRange: null,
        checkToDate: changedValues.checkToDate ?? filterInfo.checkToDate,
      });
    }

    if (Object.hasOwn(changedValues, "invoiceFromDate")) {
      form.setFieldsValue({ invoiceDateRange: null });
      updateInvoiceDateRange(null);
      setApprovalFilter({
        ...filterInfo,
        invoiceDateRange: null,
        invoiceFromDate: changedValues.invoiceFromDate ?? filterInfo?.invoiceFromDate,
      });
    }

    if (Object.hasOwn(changedValues, "invoiceToDate")) {
      form.setFieldsValue({ invoiceDateRange: null });
      updateInvoiceDateRange(null);
      setApprovalFilter({
        ...filterInfo,
        invoiceDateRange: null,
        invoiceToDate: changedValues.invoiceToDate ?? filterInfo.invoiceToDate,
      });
    }

    if (Object.hasOwn(changedValues, "purchaser") && changedValues.purchaser !== undefined) {
      form.setFieldsValue({ purchaser: changedValues.purchaser });
      setApprovalFilter({
        ...filterInfo,
        purchaser: changedValues.purchaser ?? [],
      });
    }
  };

  // Date range state management
  const { checkDateRangeValue, invoiceDateRangeValue, updateCheckDateRange, updateInvoiceDateRange } =
    useDateRangeState({
      filterInfo,
      defaultCheckDateRange: defaultCheckDateRangePreset,
      isFilterApplied,
    });

  // Date range handlers
  const { handleCheckDateRangeChange, handleInvoiceDateRangeChange, calculateDateRange } = useDateRangeHandlers({
    defaultCheckDateRangeInMonths: DEFAULT_CHECK_DATE_RANGE_IN_MONTHS,
    filterInfo,
    isFilterApplied,
    isDefaultFilter: () => true,
    form,
    onCheckDateRangeUpdate: updateCheckDateRange,
    onInvoiceDateRangeUpdate: updateInvoiceDateRange,
  });

  // Skip next useDefaultFilterSync so it doesn't revert user's preset selection
  const skipDefaultSyncRef = useRef(false);

  // Sync store when preset is selected so submit has dates (form onFinish can omit them)
  const onCheckDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      handleCheckDateRangeChange(value);
      skipDefaultSyncRef.current = true;
      if (value) {
        const { fromDate, toDate } = calculateDateRange(value, "checkDateRange");
        setApprovalFilter({
          ...filterInfo,
          checkFromDate: fromDate ?? filterInfo.checkFromDate,
          checkToDate: toDate ?? filterInfo.checkToDate,
          checkDateRange: value,
        });
      } else {
        setApprovalFilter({
          ...filterInfo,
          checkFromDate: null,
          checkToDate: null,
          checkDateRange: null,
        });
      }
    },
    [handleCheckDateRangeChange, calculateDateRange, filterInfo, setApprovalFilter]
  );

  const onInvoiceDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      handleInvoiceDateRangeChange(value);
      skipDefaultSyncRef.current = true;
      if (value) {
        const { fromDate, toDate } = calculateDateRange(value, "invoiceDateRange");
        setApprovalFilter({
          ...filterInfo,
          invoiceFromDate: fromDate ?? filterInfo.invoiceFromDate,
          invoiceToDate: toDate ?? filterInfo.invoiceToDate,
          invoiceDateRange: value,
        });
      } else {
        setApprovalFilter({
          ...filterInfo,
          invoiceFromDate: null,
          invoiceToDate: null,
          invoiceDateRange: null,
        });
      }
    },
    [handleInvoiceDateRangeChange, calculateDateRange, filterInfo, setApprovalFilter]
  );

  const handleSubmit = useCallback(
    async (values: any) => {
      const formState = form.getFieldsValue();
      const effectiveCheckDateRange =
        values.checkDateRange ??
        formState.checkDateRange ??
        filterInfo.checkDateRange ??
        defaultFilterInfo.checkDateRange;
      const effectiveInvoiceDateRange =
        values.invoiceDateRange ??
        formState.invoiceDateRange ??
        filterInfo.invoiceDateRange ??
        defaultFilterInfo.invoiceDateRange;

      // When user only changes preset and submits, store may not have re-rendered yet. Derive dates from preset if missing.
      let checkFromDate =
        values.checkFromDate ?? formState.checkFromDate ?? filterInfo.checkFromDate ?? defaultFilterInfo.checkFromDate;
      let checkToDate =
        values.checkToDate ?? formState.checkToDate ?? filterInfo.checkToDate ?? defaultFilterInfo.checkToDate;
      if (effectiveCheckDateRange && (checkFromDate == null || checkToDate == null)) {
        const derived = calculateDateRange(effectiveCheckDateRange, "checkDateRange");
        checkFromDate = checkFromDate ?? derived.fromDate;
        checkToDate = checkToDate ?? derived.toDate;
      }

      let invoiceFromDate =
        values.invoiceFromDate ??
        formState.invoiceFromDate ??
        filterInfo.invoiceFromDate ??
        defaultFilterInfo.invoiceFromDate;
      let invoiceToDate =
        values.invoiceToDate ?? formState.invoiceToDate ?? filterInfo.invoiceToDate ?? defaultFilterInfo.invoiceToDate;
      if (effectiveInvoiceDateRange && (invoiceFromDate == null || invoiceToDate == null)) {
        const derived = calculateDateRange(effectiveInvoiceDateRange, "invoiceDateRange");
        invoiceFromDate = invoiceFromDate ?? derived.fromDate;
        invoiceToDate = invoiceToDate ?? derived.toDate;
      }

      const valuesWithDates: ApprovalFilterInfo = {
        ...filterInfo,
        ...values,
        checkFromDate,
        checkToDate,
        checkDateRange: effectiveCheckDateRange,
        invoiceFromDate,
        invoiceToDate,
        invoiceDateRange: effectiveInvoiceDateRange,
      };
      setApprovalFilter(valuesWithDates);
      setCurrentPage(1);
      setIsFilterApplied(true);
      closeFilter();
      setSelectedApprovals([]);
      await fetchApprovalsList({ isLoading: false, isRefreshing: true });
    },
    [
      form,
      filterInfo,
      defaultFilterInfo,
      calculateDateRange,
      setApprovalFilter,
      setIsFilterApplied,
      fetchApprovalsList,
      closeFilter,
      setSelectedApprovals,
      setCurrentPage,
    ]
  );

  const handleClear = useCallback(async () => {
    form.resetFields();
    setApprovalFilter(getEmptyApprovalFilterInfo());
    setIsFilterApplied(false);
    setSelectedApprovals([]);
    await fetchApprovalsList({ isLoading: false, isRefreshing: true });
  }, [form, setApprovalFilter, setIsFilterApplied, fetchApprovalsList, setSelectedApprovals]);

  // Form synchronization
  useFormSync({
    form,
    filterInfo,
    defaultFilterInfo,
    isFilterApplied,
    calculateDateRange,
  });

  // Default filter synchronization (skip when user just selected a preset so selection doesn't revert)
  useDefaultFilterSync({
    form,
    filterInfo,
    defaultFilterInfo,
    isFilterApplied,
    setFilter: filter => setApprovalFilter(filter as ApprovalFilterInfo),
    skipNextSyncRef: skipDefaultSyncRef,
  });

  return (
    <Form
      form={form}
      className="m-0"
      initialValues={defaultFilterInfo}
      onFinish={handleSubmit}
      onValuesChange={onFieldsChange}
    >
      <Row className="flex-1">
        {/* Check Date Range */}
        <FilterInfoCol>
          <Division className="date-range-wrapper">
            <Flex align="center" justifyContent="space-between" className="pb-2">
              <Span className="d-flex text-sm font-medium text-grey-700">Check Date:</Span>
              <FilterDateRangeSelector
                value={checkDateRangeValue}
                fieldName="checkDateRange"
                onChange={onCheckDateRangeChange}
              />
            </Flex>
            <Division className="display-picker mb-3">
              <Division>
                <Form.Item
                  label="From Date"
                  name="checkFromDate"
                  dependencies={["checkToDate"]}
                  rules={[
                    ...(isNoDefaultDateRange ? [] : [{ required: true, message: "Required" }]),
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value && getFieldValue("checkToDate")) {
                          return Promise.reject(new Error("Check start date is required"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showClearButton={isNoDefaultDateRange}
                    minValue={DATE_CONSTANTS.checkFromDateDefaultMinValue}
                    maxValue={checkFromDateMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                  />
                </Form.Item>
              </Division>
              <Division>
                <Form.Item
                  label="To Date"
                  name="checkToDate"
                  dependencies={["checkFromDate"]}
                  rules={[
                    ...(isNoDefaultDateRange ? [] : [{ required: true, message: "Required" }]),
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value && getFieldValue("checkFromDate")) {
                          return Promise.reject(new Error("Check end date is required"));
                        }
                        const fromDate = getFieldValue("checkFromDate");
                        if (value && fromDate && dateService.isBefore(value, fromDate)) {
                          return Promise.reject(new Error("Check end date cannot be before check start date"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showClearButton={isNoDefaultDateRange}
                    minValue={checkToDateMinValue}
                    maxValue={DATE_CONSTANTS.checkFromDateDefaultMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                  />
                </Form.Item>
              </Division>
            </Division>
          </Division>
        </FilterInfoCol>

        {/* Invoice Date Range */}
        <FilterInfoCol>
          <Division className="date-range-wrapper">
            <Flex align="center" justifyContent="space-between" className="pb-2">
              <Span className="d-flex text-sm font-medium text-grey-700">Invoice Date:</Span>
              <FilterDateRangeSelector
                value={invoiceDateRangeValue}
                fieldName="invoiceDateRange"
                onChange={onInvoiceDateRangeChange}
              />
            </Flex>
            <Division className="display-picker mb-3">
              <Division>
                <Form.Item
                  label="From Date"
                  name="invoiceFromDate"
                  dependencies={["invoiceToDate"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value && getFieldValue("invoiceToDate")) {
                          return Promise.reject(new Error("Invoice start date is required"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showClearButton
                    minValue={DATE_CONSTANTS.invoiceFromDateDefaultMinValue}
                    maxValue={invoiceFromDateMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                  />
                </Form.Item>
              </Division>
              <Division>
                <Form.Item
                  label="To Date"
                  name="invoiceToDate"
                  dependencies={["invoiceFromDate"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value && getFieldValue("invoiceFromDate")) {
                          return Promise.reject(new Error("Invoice end date is required"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showClearButton
                    minValue={invoiceToDateMinValue}
                    maxValue={DATE_CONSTANTS.invoiceFromDateDefaultMaxValue}
                    dateFormat={settings?.displayDateFormat}
                    shouldCloseOnInteractOutside={true}
                    isNonModal={true}
                  />
                </Form.Item>
              </Division>
            </Division>
          </Division>
        </FilterInfoCol>

        {/* Purchaser */}
        <FilterInfoCol>
          <Form.Item name="purchaser" label="Purchaser" labelAlign="right" className="mb-3" isList={true}>
            <Select
              size="small"
              maxMenuHeight={150}
              options={purchaserOptions}
              isMultiSelect={true}
              isLoading={purchasersLookup?.isLoading}
              defaultValue={filterInfo.purchaser}
              isClearable={true}
              maxTagCount={3}
              maxTagLength={4}
            />
          </Form.Item>
        </FilterInfoCol>

        {/* Distribution Center */}
        <FilterInfoCol>
          <Form.Item name="dc" label="DC" labelAlign="right" className="mb-3" isList={true} rules={[]}>
            <Select
              size="small"
              maxMenuHeight={150}
              options={dcOptions}
              isMultiSelect={true}
              isLoading={distributionCentersLookup?.isLoading}
              defaultValue={filterInfo.dc}
              isClearable={true}
              maxTagCount={3}
              maxTagLength={4}
            />
          </Form.Item>
        </FilterInfoCol>

        {/* GL Code */}
        <FilterInfoCol>
          <Form.Item name="glCode" label="GL Code" labelAlign="right" className="mb-3" isList={true}>
            <Select
              size="small"
              maxMenuHeight={150}
              options={glCodeOptions}
              isMultiSelect={true}
              isLoading={glCodesLookup?.isLoading}
              defaultValue={filterInfo.glCode}
              isClearable={true}
              maxTagCount={3}
              maxTagLength={4}
            />
          </Form.Item>
        </FilterInfoCol>

        {/* Analyst */}
        <FilterInfoCol>
          <Form.Item name="analyst" label="Analyst" labelAlign="right" className="mb-3" isList={true}>
            <Select
              size="small"
              maxMenuHeight={150}
              options={analystOptions}
              isMultiSelect={true}
              isLoading={brandMembersLookup?.isLoading}
              defaultValue={filterInfo.analyst}
              isClearable={true}
              maxTagCount={3}
              maxTagLength={4}
            />
          </Form.Item>
        </FilterInfoCol>

        {/* Approver */}
        <FilterInfoCol>
          <Form.Item name="approver" label="Approver" labelAlign="right" className="mb-3" isList={true}>
            <Select
              size="small"
              maxMenuHeight={150}
              options={approverOptions}
              isMultiSelect={true}
              isLoading={brandMembersLookup?.isLoading}
              defaultValue={filterInfo.approver}
              isClearable={true}
              maxTagCount={3}
              maxTagLength={4}
            />
          </Form.Item>
        </FilterInfoCol>
      </Row>

      {/* Action Buttons */}
      <Flex justifyContent="space-between" gap={1} className="px-0 py-3">
        <Division>
          {isFilterApplied && (
            <Button
              type="reset"
              variant="clear"
              color="grey"
              size="medium"
              onPress={handleClear}
              isDisabled={!isFilterApplied}
              htmlTitle="Clear filters"
            >
              Clear filters
            </Button>
          )}
        </Division>
        <Division>
          <Button type="button" variant="outline" color="grey" size="medium" onPress={closeFilter} htmlTitle="Close">
            Close
          </Button>
          <Button type="submit" variant="solid" color="primary" size="medium" htmlTitle="Apply">
            Apply
          </Button>
        </Division>
      </Flex>
    </Form>
  );
};

export { ApprovalFilterForm };
